import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import MakeChargePayTrPaymentButton from 'components/payment/paytr/MakeChargePayTrPaymentButton';
import MakeChargeTapPaymentButton from 'components/payment/tap/MakeChargeTapPaymentButton';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { INVOICE, PAYMENT } from 'services/consts';

import { getClientCards, setUserDetails, setUserGeneralSettings } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import { ACCOUNT_TYPES, PAYMENT_GATEWAY_TYPE, PAYMENT_METHODS } from 'utils/constants';

import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';

import { setPaymentResult } from './ChargeAccountSlice';

/**
 * @author @yusufyldrm
 * @description This component is used to display the progress of the wizard and handles the step state
 * @param {Object} props
 * @param {Function} props.nextStep - Function to set index of current stage
 * @param {Boolean} props.isPaymentStep - Payment step state
 * @param {string} props.label - The label of the button
 * @param {Boolean} props.disabled - The disabled state of the button
 * @returns {JSX.Element} PaymentFormButton component
 * @constructor
 * @description The PaymentFormButton component for Form.
 * @example
 * <PaymentFormButton
		isPaymentStep={isPaymentStep}
		nextStep={nextStep}
		label="Continue"
		disabled={false}
	/>
 */

const PaymentFormButton = ({ isPaymentStep, nextStep, label, disabled, disabledTooltip, ...props }) => {
	const dispatch = useDispatch();
	const formMethods = useFormContext();
	const axios = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { paymentGatewayType } = useSelector((state) => state?.user.localization);
	const { clientCards } = useSelector((state) => state.user);
	const { watch, getValues, setValue, handleSubmit, formState } = formMethods;

	const selectedSavedCard = watch('selectedSavedCard')?.card;
	const selectedPaymentMethod = watch('selectedPaymentMethod');
	const total = watch('total');
	const promoCode = watch('promoCode');
	const codWallet = watch('codWallet');

	const updateBillingInfo = async () => {
		const data = watch();
		const addressInputs = [
			'address',
			'VATNumber',
			'CRNumber',
			'companyName',
			'taxOffice',
			'notVATRegistered',
			'invoiceType',
			'civilID',
			'fullName',
			'idNumber'
		];
		const isBillingInfoChanged = Object.keys(formState.dirtyFields).some((key) => addressInputs.includes(key));

		if (isBillingInfoChanged) {
			try {
				const addressData = {
					address: data.address,
					notVATRegistered: data.notVATRegistered,
					companyName: data.companyName,
					invoiceType: data.invoiceType,
					VATNumber: data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.VATNumber : null,
					CRNumber: data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.CRNumber : null,
					fullName: data.invoiceType === ACCOUNT_TYPES.PERSONAL ? data.fullName : null,
					idNumber: data.invoiceType === ACCOUNT_TYPES.PERSONAL ? data.idNumber : null,
					taxOffice: data.invoiceType === ACCOUNT_TYPES.COMPANY ? data.taxOffice : null,
					FLDocStatus: data.invoiceType === ACCOUNT_TYPES.FREELANCER ? 'UNVERIFIED' : '',
					CRDocStatus: data.invoiceType === ACCOUNT_TYPES.COMPANY ? 'UNVERIFIED' : ''
				};
				await axios.post(INVOICE.UPDATE_BILLING_INFO, addressData);
				dispatch(setUserGeneralSettings(addressData));
			} catch (error) {
				AppLogger.error(error);
			}
		}
	};

	const bankPayment = async () => {
		try {
			const { receipt } = getValues();

			if (receipt?.path) {
				await chargeCODWallet();
				updateBillingInfo();
				const { data } = await axios.post(PAYMENT.BANK_TRANSFER, {
					receipt: receipt?.path,
					code: promoCode,
					amount: total,
					codWalletAmount: codWallet || undefined
				});
				data.description = 'bankTransferReceivedInfo';
				dispatch(setPaymentResult(data));
				nextStep();
			} else {
				showError({
					header: 'error',
					message: 'error.pleaseUploadReceipt'
				});
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	const chargeCODWallet = async () => {
		if (codWallet && !watch('isCodPaymentSuccess')) {
			try {
				const { data } = await axios.post(PAYMENT.MAKE_CHARGE_COD_WALLET, {
					amount: codWallet,
					...(total === codWallet && { code: promoCode })
				});
				dispatch(
					setUserDetails({
						CODRemainingAmount: data?.remainingCODAmount
					})
				);
				setValue('isCodPaymentSuccess', true);
				if (total === codWallet) {
					nextStep();
					return;
				}
				showSuccess({
					header: 'success',
					message: 'success.codWallet'
				});
				return data;
			} catch (error) {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				setValue('isCodPaymentSuccess', false);
			}
		}
	};

	const makeCharge = async (chargeFunction) => {
		await chargeCODWallet();
		updateBillingInfo();
		handleSubmit(chargeFunction, (errors) => AppLogger.log(errors));
	};

	const chargeResult = async (data) => {
		if (data?.status === 'paid') {
			nextStep();
			if (watch('saveCard')) {
				dispatch(getClientCards({ forceSync: true }));
			}
		} else if (data?.status === 'failed') {
			showError({
				header: 'error',
				message: data?.description,
				duration: 60000
			});
		}
	};

	if (!isPaymentStep || selectedPaymentMethod === null) {
		return (
			<Button
				variant="blue"
				label={label}
				onClick={nextStep}
				disabled={disabled}
				icon={faChevronRight}
				disabledTooltip={disabledTooltip}
				iconPos="right"
				className="next-step-button"
				loading={isPaymentStep && selectedPaymentMethod === null} // if active tab is payment step and selectedPaymentMethod is not null, show loading for tab is ready to payment
				{...props}
			/>
		);
	} else {
		return (
			<>
				{paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR && (
					<>
						{(selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD || selectedSavedCard) && (
							<MakeChargePayTrPaymentButton
								label={label}
								nextStep={chargeResult}
								selectedSavedCard={clientCards?.find((card) => card?.ID === selectedSavedCard)}
								chargeCODWallet={makeCharge}
							/>
						)}
					</>
				)}

				{[PAYMENT_GATEWAY_TYPE.TAP_PAYMENT, PAYMENT_GATEWAY_TYPE.TAP_PAYMENT_UAE].includes(
					paymentGatewayType
				) && (
					<>
						{(selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD || selectedSavedCard) && (
							<MakeChargeTapPaymentButton
								nextStep={chargeResult}
								selectedSavedCard={selectedSavedCard}
								label={label}
								chargeCODWallet={makeCharge}
								disabled={
									selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD && !watch('isTapInputsValid')
								}
							/>
						)}
					</>
				)}

				{selectedPaymentMethod === PAYMENT_METHODS.BANK_CARD && (
					<Button
						label={label || 'completePayment'}
						loading={formState.isSubmitting}
						onClick={bankPayment}
						className="payment-form-button"
					/>
				)}

				{total === codWallet && <Button label={label || 'confirm'} onClick={makeCharge} />}
			</>
		);
	}
};

export default PaymentFormButton;
